/**
 * This module provides custom functionality for making authenticated API requests using Redux Toolkit Query.
 * It includes token handling and retrying requests in case of token expiration.
 *
 * @module AuthenticatedQuery
 * @preferred
 */

import {
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
  fetchBaseQuery,
  retry,
} from '@reduxjs/toolkit/query/react';
import { Mutex } from 'async-mutex';
import { getAuth } from 'firebase/auth';
import qs from 'qs';
import { toast } from 'react-toastify';
import { logout, refreshToken } from 'src/auth';
import { getBaseApiUrl } from 'src/config/apiConfig';

/**
 * A mutex for ensuring only one token refresh operation occurs at a time.
 */
const mutex = new Mutex();

/**
 * The base query for making API requests. It includes handling authorization headers.
 */
const baseQuery = fetchBaseQuery({
  paramsSerializer: (params) => {
    return qs.stringify(params, { arrayFormat: 'repeat' });
  },
  prepareHeaders: async (headers: any) => {
    const token: string | null =
      (await getAuth()?.currentUser?.getIdToken?.()) ?? '';
    if (token) {
      headers.set('authorization', `Bearer ${token}`);
    }
    headers.set('x-app-slug', 'admin-console');
    return headers;
  },
  baseUrl: getBaseApiUrl(),
});

/** The base query with any thrown exception returned as a `CUSTOM_ERROR` result. */
const safeBaseQuery: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  try {
    return await baseQuery(args, api, extraOptions);
  } catch (err) {
    console.error('API request threw before completing', { args, err });
    return { error: { status: 'CUSTOM_ERROR', error: String(err) } };
  }
};

/** The safe base query with failed GET requests retried at most twice, with backoff. */
const staggeredBaseQuery = retry(safeBaseQuery, {
  retryCondition: (_error, args, { attempt }) => {
    // Maximum of 2 retries (3 total attempts)
    if (attempt > 2) return false;

    const method =
      typeof args === 'string' ? 'GET' : args.method?.toUpperCase() || 'GET';
    return method === 'GET';
  },
});

/**
 * A custom base query function that handles token expiration and refreshes the token when necessary.
 *
 * @param args - The API request arguments.
 * @param api - The API instance.
 * @param extraOptions - Extra options for the query.
 * @returns The result of the API request.
 */
export const baseQueryWithTokenExpirationCheck: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  // Wait until the mutex is available without locking it.
  await mutex.waitForUnlock();

  let result = await staggeredBaseQuery(args, api, extraOptions);

  if (result.error && result.error.status === 401) {
    // Checking whether the mutex is locked.
    if (!mutex.isLocked()) {
      const release = await mutex.acquire();

      try {
        // Get a new access token.
        const refreshResult = await refreshToken();

        if (
          refreshResult &&
          refreshResult.accessToken &&
          refreshResult.refreshToken
        ) {
          // Retry the initial query.
          result = await staggeredBaseQuery(args, api, extraOptions);
        } else {
          throw new Error('Refresh Token Error');
        }
      } catch {
        toast.dismiss();
        toast.error('Session expired. Please log in again.');
        setTimeout(function () {
          logout();
        }, 2000);

        // We can bail out of retries if we know it is going to be redundant - not authenticated at all.
        retry.fail(result.error);
      } finally {
        // Release must be called once the mutex should be released again.
        release();
      }
    } else {
      // Wait until the mutex is available without locking it.
      await mutex.waitForUnlock();
      result = await staggeredBaseQuery(args, api, extraOptions);
    }
  }

  return result;
};
